/*
  LetsGetDigital theme overrides for Lacorine (Prestige)
  ------------------------------------------------------
  Loaded BEFORE theme.js (see layout/theme.liquid). theme.js only defines a
  custom element if it is not already registered, so the two classes below
  replace the stock Prestige versions without editing theme.js itself.

  Ported from the live theme "Cart UX Redesign - 16 Oct 2026", where these
  changes had been made directly inside assets/theme.js:
   - product-sticky-bar: visibility recalculated on scroll/resize/pageshow
     (throttled with requestAnimationFrame) using getBoundingClientRect, so the
     sticky add-to-cart bar shows reliably instead of relying on
     IntersectionObserver entries alone.
   - product-recommendations: supports data-section-id, guards against stale
     responses, and keeps the element visible inside <cart-drawer> when no
     recommendations are returned (so the cart drawer's pre-rendered content is
     not removed).
*/

class LgdProductStickyBar extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(this.#onFormVisibilityChange.bind(this));
  #formElement;
  #footerElement;
  #latestFooterCondition = false;
  #latestFormCondition = false;
  #stickyVisibilityFrame = null;
  #stickyVisibilityController;
  #stickyVisibilityHandler = () => {
    if (this.#stickyVisibilityFrame === null) {
      this.#stickyVisibilityFrame = requestAnimationFrame(() => {
        this.#stickyVisibilityFrame = null;
        this.#onFormVisibilityChange();
      });
    }
  };

  connectedCallback() {
    this.#formElement = document.forms[this.getAttribute("form")];
    this.#footerElement = document.querySelector(".shopify-section--footer");

    if (this.#formElement) {
      this.#intersectionObserver.observe(this.#formElement);
      this.#intersectionObserver.observe(this.#footerElement);
      this.#stickyVisibilityController = new AbortController();
      const options = { passive: true, signal: this.#stickyVisibilityController.signal };
      for (const eventName of ["scroll", "resize", "pageshow"]) {
        window.addEventListener(eventName, this.#stickyVisibilityHandler, options);
      }
      this.#stickyVisibilityHandler.call(this);
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#stickyVisibilityController?.abort();
    cancelAnimationFrame(this.#stickyVisibilityFrame);
    this.#stickyVisibilityFrame = null;
  }

  #onFormVisibilityChange() {
    const formRect = this.#formElement.getBoundingClientRect();
    const footerRect = this.#footerElement.getBoundingClientRect();

    this.#latestFormCondition = formRect.bottom < 0;
    this.#latestFooterCondition =
      footerRect.top > document.documentElement.clientHeight ||
      footerRect.bottom < 0 ||
      footerRect.left > document.documentElement.clientWidth ||
      footerRect.right < 0;

    this.classList.toggle("is-visible", this.#latestFooterCondition && this.#latestFormCondition);
  }
}

class LgdProductRecommendations extends HTMLElement {
  #isLoaded = false;
  #isLoadingRecommendations = false;

  connectedCallback() {
    this.#loadRecommendations();
  }

  async #loadRecommendations() {
    if (this.#isLoaded || this.#isLoadingRecommendations) {
      return;
    }

    const sectionId = this.getAttribute("data-section-id") || this.closest(".shopify-section")?.id.replace("shopify-section-", "");
    const intent = this.getAttribute("intent") || "related";
    const product = this.getAttribute("product");
    const limit = this.getAttribute("limit") || "4";

    if (!sectionId || !product) {
      if (this.closest("cart-drawer") && this.childElementCount > 0) {
        this.hidden = false;
      }
      return;
    }

    this.#isLoadingRecommendations = true;

    try {
      const url = `${Shopify.routes.root}recommendations/products?product_id=${product}&limit=${limit}&section_id=${encodeURIComponent(sectionId)}&intent=${intent}`;
      const response = await fetch(url, { priority: intent === "related" ? "low" : "auto" });

      if (!response.ok) {
        return;
      }

      const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html");
      const productRecommendationsElement = tempDiv.querySelector("product-recommendations");

      if (
        !this.isConnected ||
        this.getAttribute("product") !== product ||
        (this.getAttribute("intent") || "related") !== intent ||
        (this.getAttribute("limit") || "4") !== limit ||
        (this.getAttribute("data-section-id") || this.closest(".shopify-section")?.id.replace("shopify-section-", "")) !== sectionId ||
        !productRecommendationsElement
      ) {
        return;
      }

      if (productRecommendationsElement.childElementCount > 0) {
        this.replaceChildren(...document.importNode(productRecommendationsElement, true).childNodes);
        this.hidden = false;
      } else if (this.closest("cart-drawer")) {
        this.hidden = false;
      } else {
        this.remove();
      }

      this.#isLoaded = true;
    } catch {
      // Network or parsing failure: leave any pre-rendered content in place.
    } finally {
      this.#isLoadingRecommendations = false;

      if (!this.#isLoaded && this.isConnected && this.closest("cart-drawer") && this.childElementCount > 0) {
        this.hidden = false;
      }
    }
  }
}

window.customElements.get("product-sticky-bar") || window.customElements.define("product-sticky-bar", LgdProductStickyBar);
window.customElements.get("product-recommendations") || window.customElements.define("product-recommendations", LgdProductRecommendations);
